import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import { API_URL } from "../../utils/config";
import { mapejos } from "../../utils/Mapejos";
import { formatDate, formatTime, generateAddress } from "../../utils/Utilitats";
import "./seleccionartreball.css";

const PAGE_SIZES = [10, 25, 50];

function SeleccionarTreball({
  onTreballSelect,
  selectedTreball,
  idClient,
  totsElsClients = false,
  estatInicial,
  excludeId,
  onTreballsChange,
  onTreballDoubleClick,
  onConsultar,
  onModificar,
  onEsborrar,
  onTasques,
  onContactes,
  onOfertes,
  onAgrupacions,
  onOrganitzacio,
  onResum,
  onLogistica,
  reloadToken = 0,
  filters,
}) {
  const { empresaSeleccionada, rolUsuari } = useUserContext();
  const pucConsultar = tincPermis(rolUsuari, "CONSULTAR");
  const pucModificar = tincPermis(rolUsuari, "MODIFICAR");
  const pucEsborrar = tincPermis(rolUsuari, "ESBORRAR");
  const teAccionsFila = !!(onConsultar || onModificar || onEsborrar || onTasques);

  const [treballs, setTreballs] = useState([]);
  const [filteredTreballs, setFilteredTreballs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [localFilterText, setLocalFilterText] = useState("");
  const idClientInicial = totsElsClients
    ? null
    : idClient?.idClients || (typeof idClient === "number" ? idClient : null);
  const [localEstatFilters, setLocalEstatFilters] = useState(
    estatInicial !== undefined ? estatInicial : idClientInicial ? "" : "Pendent,En curs"
  );
  const [localTipusTreballFilter, setLocalTipusTreballFilter] = useState("");
  const [clients, setClients] = useState([]);
  const [localSelectedClient, setLocalSelectedClient] = useState(idClientInicial || null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [menuObert, setMenuObert] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  const [isNarrow, setIsNarrow] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const selectedRowRef = useRef(null);

  const filterText = filters?.filterText !== undefined ? filters.filterText : localFilterText;
  const setFilterText = filters?.setFilterText ?? setLocalFilterText;
  const estatFilters = filters?.estatFilters !== undefined ? filters.estatFilters : localEstatFilters;
  const setEstatFilters = filters?.setEstatFilters ?? setLocalEstatFilters;
  const tipusTreballFilter = filters?.tipusTreballFilter !== undefined ? filters.tipusTreballFilter : localTipusTreballFilter;
  const setTipusTreballFilter = filters?.setTipusTreballFilter ?? setLocalTipusTreballFilter;
  const selectedClient = filters?.clientFilter !== undefined ? filters.clientFilter : localSelectedClient;
  const setSelectedClient = filters?.setClientFilter ?? setLocalSelectedClient;

  const handleTreballClick = (treball) => {
    onTreballSelect?.(treball);
  };

  const handleTreballDoubleClick = (treball) => {
    if (onConsultar) onConsultar(treball);
    else onTreballDoubleClick?.(treball);
  };

  const sortTreballs = (rows, { key, direction }) => {
    if (!key) return rows;
    return [...rows].sort((a, b) => {
      const va = a[key] ?? "";
      const vb = b[key] ?? "";
      if (va < vb) return direction === "asc" ? -1 : 1;
      if (va > vb) return direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
    setPage(1);
  };

  useEffect(() => {
    const fetchTreballs = async () => {
      if (!empresaSeleccionada?.IdEmpresa) {
        console.error("No hi ha cap empresa seleccionada.");
        return;
      }
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/Treballs.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            idEmpresa: empresaSeleccionada.IdEmpresa,
            ...(selectedClient ? { idClient: selectedClient } : {}),
          }),
        });

        const result = await response.json();
        if (result.success) {
          setTreballs(result.treballs);
          setFilteredTreballs(result.treballs);
        } else {
          console.error("Error:", result.message);
          setTreballs([]);
          setFilteredTreballs([]);
        }
      } catch (error) {
        console.error("Error carregant treballs:", error);
        setTreballs([]);
        setFilteredTreballs([]);
      } finally {
        setLoading(false);
      }
    };
    fetchTreballs();
  }, [empresaSeleccionada, selectedClient, reloadToken]);

  useEffect(() => {
    onTreballsChange?.(filteredTreballs);
  }, [filteredTreballs, onTreballsChange]);

  useEffect(() => {
    const fetchClients = async () => {
      if (!empresaSeleccionada?.IdEmpresa) return;
      try {
        const response = await fetch(`${API_URL}/Clientstreballs.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idEmpresa: empresaSeleccionada.IdEmpresa }),
        });
        const result = await response.json();
        if (result.success) {
          setClients(result.clients);
        } else {
          setClients([]);
        }
      } catch (error) {
        console.error("Error carregant clients:", error);
        setClients([]);
      }
    };
    fetchClients();
  }, [empresaSeleccionada]);

  useEffect(() => {
    const filtered = treballs.filter((treball) => {
      const matchesText =
        filterText === "" ||
        (treball.NomClient || "").toLowerCase().includes(filterText.toLowerCase()) ||
        (treball.Descripcio || "").toLowerCase().includes(filterText.toLowerCase()) ||
        String(treball.idTreballIntern || "").toLowerCase().includes(filterText.toLowerCase()) ||
        generateAddress(treball).toLowerCase().includes(filterText.toLowerCase());

      const matchesEstat =
        estatFilters === "" || estatFilters.split(",").includes(treball.Estat);

      const matchesClient =
        !selectedClient || Number(treball.Clients_idClients) === Number(selectedClient);

      const matchesTipusTreball =
        tipusTreballFilter === "" || treball.TipusTreball === tipusTreballFilter;

      const noExclos = excludeId == null || Number(treball.idTreballs) !== Number(excludeId);

      return matchesText && matchesEstat && matchesClient && matchesTipusTreball && noExclos;
    });

    setFilteredTreballs(filtered);
    setPage(1);
  }, [filterText, estatFilters, selectedClient, tipusTreballFilter, treballs, excludeId]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const apply = () => setIsNarrow(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    const tancar = (e) => {
      if (e.target.closest(".dv2-menu") || e.target.closest(".dv2-menuBtn")) return;
      setMenuObert(null);
    };
    document.addEventListener("mousedown", tancar);
    return () => document.removeEventListener("mousedown", tancar);
  }, []);

  const obrirMenu = (e, treball) => {
    e.stopPropagation();
    const id = treball.idTreballs;
    if (menuObert === id) {
      setMenuObert(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const ample = 220;
    const alt = 360;
    const marge = 8;
    let top = rect.bottom + 4;
    if (top + alt > window.innerHeight - marge) {
      top = Math.max(marge, rect.top - alt - 4);
    }
    let left = rect.right - ample;
    if (left < marge) left = marge;
    if (left + ample > window.innerWidth - marge) {
      left = window.innerWidth - ample - marge;
    }
    setMenuPos({ top, left });
    setMenuObert(id);
  };

  const sortedTreballs = sortTreballs(filteredTreballs, sortConfig);
  const total = sortedTreballs.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const paginaActual = Math.min(page, totalPages);
  const inici = (paginaActual - 1) * pageSize;
  const paginaItems = sortedTreballs.slice(inici, inici + pageSize);

  const paginesVisibles = () => {
    const pages = [];
    const max = totalPages;
    const actual = paginaActual;
    const afegir = (n) => {
      if (!pages.includes(n)) pages.push(n);
    };
    afegir(1);
    for (let i = actual - 1; i <= actual + 1; i += 1) {
      if (i > 1 && i < max) afegir(i);
    }
    if (max > 1) afegir(max);
    return pages.sort((a, b) => a - b);
  };

  const treballMenu = menuObert ? sortedTreballs.find((t) => t.idTreballs === menuObert) : null;

  const tancarI = (fn) => {
    setMenuObert(null);
    fn?.();
  };

  const th = (key, label) => (
    <th className={sortConfig.key === key ? "is-sorted" : ""} onClick={() => handleSort(key)}>
      {label} {sortConfig.key === key ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
    </th>
  );

  const renderAccions = (treball) => (
    <div className="dv2-table-actions">
      <button
        type="button"
        className={`dv2-menuBtn ${menuObert === treball.idTreballs ? "is-open" : ""}`}
        aria-label="Accions"
        onClick={(e) => obrirMenu(e, treball)}
      >
        …
      </button>
    </div>
  );

  return (
    <div className="dv2-listBody">
      <div className="dv2-filterBar">
        <div className="dv2-filter dv2-filter--grow">
          <span className="dv2-filter-label">Cerca</span>
          <input
            type="text"
            className="dv2-input dv2-input-search"
            placeholder="Cerca per client, adreça…"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value.toLowerCase())}
          />
        </div>
        <label className="dv2-filter">
          <span className="dv2-filter-label">Estat</span>
          <select className="dv2-select" value={estatFilters} onChange={(e) => setEstatFilters(e.target.value)}>
            <option value="">Tots</option>
            <option value="Pendent,En curs">Pendent + En curs</option>
            <option value="Pendent">Pendent</option>
            <option value="En curs">En curs</option>
            <option value="Acabat">Acabat</option>
            <option value="Rebutjat">Rebutjat</option>
          </select>
        </label>
        <label className="dv2-filter">
          <span className="dv2-filter-label">Tipus</span>
          <select
            className="dv2-select"
            value={tipusTreballFilter}
            onChange={(e) => setTipusTreballFilter(e.target.value)}
          >
            <option value="">Tots</option>
            {Object.entries(mapejos.tipusTreballMap).map(([key, value]) => (
              <option key={key} value={key}>
                {value}
              </option>
            ))}
          </select>
        </label>
        <label className="dv2-filter">
          <span className="dv2-filter-label">Client</span>
          <select
            className="dv2-select"
            value={selectedClient || ""}
            onChange={(e) => setSelectedClient(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">Tots els clients</option>
            {clients.map((c) => (
              <option key={c.idClients} value={c.idClients}>
                {c.NomClient}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading ? (
        <p className="dv2-empty">Carregant treballs…</p>
      ) : sortedTreballs.length === 0 ? (
        <p className="dv2-empty">No s'ha trobat cap treball.</p>
      ) : (
        <>
      {isNarrow ? (
        <div className="dv2-recordList" style={{ display: "flex" }}>
          {paginaItems.map((treball) => {
            const isSel = selectedTreball?.idTreballs === treball.idTreballs;
            return (
              <article
                key={treball.idTreballs}
                className={`dv2-record ${isSel ? "is-selected" : ""}`}
                onClick={() => handleTreballClick(treball)}
                onDoubleClick={() => handleTreballDoubleClick(treball)}
              >
                <div>
                  <p className="dv2-record-title">
                    {[treball.idTreballIntern, treball.NomClient].filter(Boolean).join(" · ") || "—"}
                  </p>
                  <p className="dv2-record-meta">
                    {[
                      formatDate(treball.DataAlta),
                      treball.Descripcio,
                      treball.Poblacio,
                      treball.TipusTreball,
                      treball.Estat,
                    ]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </p>
                </div>
                {teAccionsFila && renderAccions(treball)}
              </article>
            );
          })}
        </div>
      ) : (
        <div className="dv2-tableWrap">
          <table className="dv2-table">
            <thead>
              <tr>
                {th("idTreballIntern", "Treball")}
                {th("DataAlta", "Data alta")}
                <th>Hora</th>
                {th("NomClient", "Client")}
                {th("Descripcio", "Descripció")}
                {th("PersonaContacte", "Contacte")}
                {th("Telefon", "Telèfon")}
                {th("Poblacio", "Població")}
                {th("TipusTreball", "Tipus")}
                {th("Estat", "Estat")}
                {th("idPresIntern", "Pressupost")}
                {th("PrioritatTreball", "Prioritat")}
                {teAccionsFila && <th className="dv2-table-actions"></th>}
              </tr>
            </thead>
            <tbody>
              {paginaItems.map((treball) => {
                const isSel = selectedTreball?.idTreballs === treball.idTreballs;
                return (
                  <tr
                    key={treball.idTreballs}
                    ref={isSel ? selectedRowRef : null}
                    className={isSel ? "is-selected" : ""}
                    onClick={() => handleTreballClick(treball)}
                    onDoubleClick={() => handleTreballDoubleClick(treball)}
                  >
                    <td>{treball.idTreballIntern}</td>
                    <td>{formatDate(treball.DataAlta)}</td>
                    <td>{formatTime(treball.DataAlta)}</td>
                    <td>{treball.NomClient || "—"}</td>
                    <td className="dv2-cell-clip" title={treball.Descripcio || ""}>{treball.Descripcio || "—"}</td>
                    <td>{treball.PersonaContacte || "—"}</td>
                    <td>{treball.Telefon || "—"}</td>
                    <td>{treball.Poblacio || "—"}</td>
                    <td>{treball.TipusTreball}</td>
                    <td>{treball.Estat}</td>
                    <td>{treball.idPresIntern || "—"}</td>
                    <td>{treball.PrioritatTreball}</td>
                    {teAccionsFila && <td className="dv2-table-actions">{renderAccions(treball)}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
          )}
          <div className="dv2-pagination">
            <span>
              Mostrant {inici + 1} a {Math.min(inici + pageSize, total)} de {total} resultats
            </span>
            <div className="dv2-pagination-pages">
              <button type="button" className="dv2-pageBtn dv2-pageBtn--nav" disabled={paginaActual <= 1} onClick={() => setPage(1)}>
                «
              </button>
              <button
                type="button"
                className="dv2-pageBtn dv2-pageBtn--nav"
                disabled={paginaActual <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ‹
              </button>
              {paginesVisibles().map((n, idx, arr) => (
                <React.Fragment key={n}>
                  {idx > 0 && n - arr[idx - 1] > 1 && <span>…</span>}
                  <button
                    type="button"
                    className={`dv2-pageBtn ${n === paginaActual ? "is-active" : ""}`}
                    onClick={() => setPage(n)}
                  >
                    {n}
                  </button>
                </React.Fragment>
              ))}
              <button
                type="button"
                className="dv2-pageBtn dv2-pageBtn--nav"
                disabled={paginaActual >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                ›
              </button>
              <button
                type="button"
                className="dv2-pageBtn dv2-pageBtn--nav"
                disabled={paginaActual >= totalPages}
                onClick={() => setPage(totalPages)}
              >
                »
              </button>
            </div>
            <label className="dv2-filter">
              <span className="dv2-filter-label">Files per pàgina</span>
              <select
                className="dv2-select"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </>
      )}

      {teAccionsFila &&
        treballMenu &&
        menuPos &&
        createPortal(
          <div className="dv2-menu dv2-menu--fixed" style={{ top: menuPos.top, left: menuPos.left }}>
            {pucConsultar && onConsultar && (
              <button type="button" className="dv2-menu-item" onClick={() => tancarI(() => onConsultar(treballMenu))}>
                Consultar
              </button>
            )}
            {pucModificar && onModificar && (
              <button type="button" className="dv2-menu-item" onClick={() => tancarI(() => onModificar(treballMenu))}>
                Modificar
              </button>
            )}
            {pucModificar && onTasques && (
              <button type="button" className="dv2-menu-item" onClick={() => tancarI(() => onTasques(treballMenu))}>
                Tasques
              </button>
            )}
            {pucModificar && onContactes && (
              <button type="button" className="dv2-menu-item" onClick={() => tancarI(() => onContactes(treballMenu))}>
                Contactes
              </button>
            )}
            {pucModificar && onOfertes && (
              <button type="button" className="dv2-menu-item" onClick={() => tancarI(() => onOfertes(treballMenu))}>
                Ofertes
              </button>
            )}
            {pucModificar && onAgrupacions && (
              <button type="button" className="dv2-menu-item" onClick={() => tancarI(() => onAgrupacions(treballMenu))}>
                Agrupacions
              </button>
            )}
            {pucModificar && onOrganitzacio && (
              <button type="button" className="dv2-menu-item" onClick={() => tancarI(() => onOrganitzacio(treballMenu))}>
                Organització
              </button>
            )}
            {pucModificar && onResum && (
              <button type="button" className="dv2-menu-item" onClick={() => tancarI(() => onResum(treballMenu))}>
                Resum
              </button>
            )}
            {onLogistica && (
              <button type="button" className="dv2-menu-item" onClick={() => tancarI(() => onLogistica(treballMenu))}>
                Logística
              </button>
            )}
            {pucEsborrar && onEsborrar && (
              <button
                type="button"
                className="dv2-menu-item is-danger"
                onClick={() => tancarI(() => onEsborrar(treballMenu))}
              >
                Esborrar
              </button>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}

export default SeleccionarTreball;
